import { ConnectedUser } from '@ucc/admin-global-state'
import { BillingRoles, buildRolesBasedSecurityContext, Roles, SecurityContext } from '@ucc/unified-admin-common'

export enum Privileges {
  AppLinks = 'app-links-access',
  Billing = 'billing',
  CallQueueLinks = 'call-queue-links-access',
  E911V3SelfMigration = 'e911-v3-self-migration',
  Setup = 'setup',
  SetupG2R = 'setup-g2r',
  SwitchBack = 'switch-back',
  ViewActivityTab = 'view-activity-tab',
  ViewGroupsTab = 'view-groups-tabs',
  ViewManageSettingsTemplatesTab = 'view-manage-settings-templates-tab',
  ViewProductsTab = 'view-products-tab',
  ViewReportsTab = 'view-reports-tab',
  ViewRoleAndPermissionsTab = 'view-role-and-permissions-tab',
  ViewSettingsAssistTab = 'view-settings-assist-tab',
  ViewSettingsPhonesTab = 'view-settings-phones-tab',
  ViewSettingsTab = 'view-settings-tabs',
  ViewUserSyncTab = 'view-user-sync-tab',
  ViewUsersTab = 'view-users-tab',

  // Billing tabs
  ViewQuotesTab = 'view-quotes-tab',
  ViewInvoicesTab = 'view-invoices-tab',
  ViewPaymentsDueTab = 'view-payments-due-tab',
  ViewPaymentMethodsTab = 'view-payments-methods-tab',
  ViewAddressBookTab = 'view-address-book-tab',
  ViewTaxExemptionTab = 'view-tax-examption-tab',
  ViewSubscriptionsTab = 'view-subscriptions-tab',

  // Gtc Billing
  ViewGtcInvoicesTab = 'view-gtc-invoices-tab',
  ViewGtcPaymentsOptionsTab = 'view-gtc-payments-due-tab',
  ViewGtcBillingDetails = 'view-gtc-billing-details',
}

const ManageUsersSet = [
  Privileges.SetupG2R,
  Privileges.ViewUsersTab,
  Privileges.ViewGroupsTab,
  Privileges.ViewProductsTab,
  Privileges.ViewSettingsTab,
  Privileges.ViewSettingsAssistTab,
  Privileges.ViewActivityTab,
  Privileges.ViewUserSyncTab,
]
const ManageGroupsSet = [
  Privileges.SetupG2R,
  Privileges.ViewUsersTab,
  Privileges.ViewGroupsTab,
  Privileges.ViewProductsTab,
  Privileges.ViewActivityTab,
]
const ManageSettingsSet = [
  Privileges.SetupG2R,
  Privileges.ViewUsersTab,
  Privileges.ViewProductsTab,
  Privileges.ViewSettingsPhonesTab,
  Privileges.ViewSettingsTab,
  Privileges.ViewSettingsAssistTab,
  Privileges.ViewActivityTab,
]
const ManageSettingsProfilesSet = [
  Privileges.SetupG2R,
  Privileges.ViewUsersTab,
  Privileges.ViewSettingsPhonesTab,
  Privileges.ViewSettingsTab,
  Privileges.ViewActivityTab,
  Privileges.ViewManageSettingsTemplatesTab,
]
const ManageSeatsSet = [
  Privileges.SetupG2R,
  Privileges.ViewUsersTab,
  Privileges.ViewProductsTab,
  Privileges.ViewActivityTab,
]
const ManageDeviceGroupsSet = [
  Privileges.SetupG2R,
  Privileges.ViewUsersTab,
  Privileges.ViewProductsTab,
  Privileges.ViewSettingsAssistTab,
  Privileges.ViewActivityTab,
]
const RunReportsSet = [
  Privileges.SetupG2R,
  Privileges.ViewUsersTab,
  Privileges.ViewProductsTab,
  Privileges.ViewActivityTab,
  Privileges.ViewReportsTab,
]
const AdminSet = [
  Privileges.AppLinks,
  Privileges.CallQueueLinks,
  Privileges.Setup,
  Privileges.SetupG2R,
  Privileges.ViewRoleAndPermissionsTab,
]
const PartnerSet = [Privileges.E911V3SelfMigration, Privileges.SwitchBack]
const BillingAdminSet = [
  Privileges.Billing,
  Privileges.ViewActivityTab,
  Privileges.ViewQuotesTab,
  Privileges.ViewInvoicesTab,
  Privileges.ViewPaymentsDueTab,
  Privileges.ViewPaymentMethodsTab,
  Privileges.ViewAddressBookTab,
  Privileges.ViewTaxExemptionTab,
  Privileges.ViewSubscriptionsTab,
]
const GtcBillingViewSet = [
  Privileges.ViewGtcInvoicesTab,
  Privileges.ViewGtcPaymentsOptionsTab,
  Privileges.ViewGtcBillingDetails,
]
const SystemUserSet = [
  ...AdminSet,
  ...BillingAdminSet,
  ...ManageDeviceGroupsSet,
  ...ManageGroupsSet,
  ...ManageSeatsSet,
  ...ManageSettingsProfilesSet,
  ...ManageSettingsSet,
  ...ManageUsersSet,
  ...PartnerSet,
  ...RunReportsSet,
  ...GtcBillingViewSet,
]

export const getPrivilegesForBillingRoles = (role: string): string[] => {
  switch (role) {
    /** ---------- billingRoles ------------- */
    case BillingRoles.AddModifyBillingAdmins:
      return [Privileges.ViewActivityTab, Privileges.ViewUsersTab]
    case BillingRoles.CancelSubscriptions:
      return [Privileges.ViewActivityTab, Privileges.ViewSubscriptionsTab]
    case BillingRoles.CreateEditCustomBillingAdminRole:
      return [Privileges.ViewActivityTab, Privileges.ViewRoleAndPermissionsTab]
    case BillingRoles.EditAddresses:
      return [Privileges.ViewActivityTab, Privileges.ViewAddressBookTab]
    case BillingRoles.EditPaymentMethods:
      return [Privileges.ViewActivityTab, Privileges.ViewPaymentMethodsTab]
    case BillingRoles.EditSubscriptions:
      return [Privileges.ViewActivityTab, Privileges.ViewSubscriptionsTab]
    case BillingRoles.EditTaxExemption:
      return [Privileges.ViewActivityTab, Privileges.ViewTaxExemptionTab]
    case BillingRoles.ParentAccess:
      return [Privileges.ViewActivityTab]
    case BillingRoles.PayInvoices:
      return [Privileges.ViewActivityTab, Privileges.ViewInvoicesTab, Privileges.ViewPaymentsDueTab]
    case BillingRoles.ProcessQuotes:
      return [Privileges.ViewActivityTab, Privileges.ViewQuotesTab]
    case BillingRoles.ViewAddresses:
      return [Privileges.ViewActivityTab, Privileges.ViewAddressBookTab]
    case BillingRoles.ViewInvoices:
      return [Privileges.ViewActivityTab, Privileges.ViewInvoicesTab]
    case BillingRoles.ViewPaymentMethods:
      return [Privileges.ViewActivityTab, Privileges.ViewPaymentMethodsTab]
    case BillingRoles.ViewQuotes:
      return [Privileges.ViewActivityTab, Privileges.ViewQuotesTab]
    case BillingRoles.ViewSubscriptions:
      return [Privileges.ViewActivityTab, Privileges.ViewSubscriptionsTab]
    case BillingRoles.ViewTaxExemption:
      return [Privileges.ViewActivityTab, Privileges.ViewTaxExemptionTab]
    case BillingRoles.ViewTIB:
      return [Privileges.ViewActivityTab]
    default:
      return []
  }
}

export const getPrivilegesForRole = (role: string): string[] => {
  switch (role) {
    /** ---------- boss and gtc-billing billing roles included in adminRoles ------------- */
    case Roles.ViewInvoices:
      return [...GtcBillingViewSet, Privileges.ViewInvoicesTab]
    case Roles.ViewAndPayInvoices:
      return [...GtcBillingViewSet]

    /** ---------- adminRoles ------------- */
    case Roles.BillingAdmin:
      return BillingAdminSet
    case Roles.ManageUsers:
      return ManageUsersSet
    case Roles.ManageGroups:
      return ManageGroupsSet
    case Roles.ManageSettings:
      return ManageSettingsSet
    case Roles.ManageSettingsProfiles:
      return ManageSettingsProfilesSet
    case Roles.ManageSeats:
      return ManageSeatsSet
    case Roles.ManageDeviceGroups:
      return ManageDeviceGroupsSet
    case Roles.RunReports:
      return RunReportsSet
    case Roles.Admin:
      return AdminSet
    case Roles.SystemUser:
      return SystemUserSet
    case Roles.Partner:
      return PartnerSet
    case Roles.SuperAdmin:
      return PartnerSet // SuperAdmin automatically is broken out into all admin roles in EA, this just adds the additional privileges outside the break-out.
    default:
      return []
  }
}

export const buildSecurityContextFromUserAndAccount = (
  user: ConnectedUser,
  accountKey: string,
  managedAdminRoles?: string[],
  managedBillingRoles?: string[],
): SecurityContext => {
  const activeAccount = (user.accounts || []).find((account) => account.key === accountKey)
  const adminRoles = managedAdminRoles || activeAccount?.adminRoles || []
  const billingRoles = managedBillingRoles || activeAccount?.billingRoles || []
  const systemRoles = user?.systemRoles || []

  const adminAndSystemEffectiveRoles = [...adminRoles, ...systemRoles]
  const adminAndSystemPrivileges = adminAndSystemEffectiveRoles.flatMap((role) => getPrivilegesForRole(role))

  const billingPrivileges = billingRoles.flatMap((role) => getPrivilegesForBillingRoles(role))

  const applicablePrivileges = [...adminAndSystemPrivileges, ...billingPrivileges]
  return buildRolesBasedSecurityContext(Privileges, applicablePrivileges)
}
